import React, { CSSProperties } from 'react';
import { Button, Col, Empty, Row, Spin, Tooltip } from 'antd';
import { Column } from '@ant-design/plots';
import {
  QuestionCircleOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  PushpinOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Filters, FiltersCard, ITableFilters } from '@components/Filters';
import * as S from '../styles';

interface IChartDataItem {
  date: string;
  value: number;
  category: string;
  type: string;
}

interface ChartDatum {
  category?: string;
  [key: string]: unknown;
}

interface TextAnnotation {
  type: 'text';
  position: [string, number];
  style: {
    textAlign: 'center';
    fontSize: number;
    fill: string;
  };
  content: string;
  offsetX?: number;
  rotate?: number;
  offsetY: number;
  tooltip: false;
}

interface BillingChartCardProps {
  title: string;
  tooltipText: string;
  isLoading: boolean;
  hasData: boolean;
  showFilters: boolean;
  onToggleFilters: () => void;
  filtersElement: ITableFilters[];
  filteredChartData: IChartDataItem[];
  groupedCategories: string[];
  visibleCategories: Set<string>;
  chartAnnotations: TextAnnotation[];
  getChartColor: (datum: ChartDatum) => string;
  getCategoryColor: (category: string) => string;
  handleCategoryToggle: (category: string) => void;
  handleCategoryIsolate: (category: string) => void;
  formatCurrency: (value: number) => string;
  formatAxisY: (value: string) => string | number;
  theme: { colors?: { text?: string } } | null;
}

const styles: { [key: string]: CSSProperties } = {
  spinner: {
    width: '100%',
    padding: '5rem 1rem',
    margin: 16,
  },
};

export const BillingChartCard: React.FC<BillingChartCardProps> = ({
  title,
  tooltipText,
  isLoading,
  hasData,
  showFilters,
  onToggleFilters,
  filtersElement,
  filteredChartData,
  groupedCategories,
  visibleCategories,
  chartAnnotations,
  getChartColor,
  getCategoryColor,
  handleCategoryToggle,
  handleCategoryIsolate,
  formatCurrency,
  formatAxisY,
  theme,
}) => {
  const { t } = useTranslation(['monthly-billing']);

  const renderCustomLegend = () => {
    return (
      <S.LegendContainer>
        {groupedCategories.map(category => {
          const isVisible = visibleCategories.has(category);
          const color = getCategoryColor(category);
          const isIsolated =
            visibleCategories.size === 1 && visibleCategories.has(category);

          const getIcon = () => {
            if (!isVisible) {
              return <EyeInvisibleOutlined />;
            }
            if (isIsolated) {
              return <PushpinOutlined />;
            }
            return <EyeOutlined />;
          };

          return (
            <S.LegendItem key={category}>
              <S.LegendTag
                $isVisible={isVisible}
                $color={color}
                onClick={() => handleCategoryToggle(category)}
                title={category}
              >
                {category}
              </S.LegendTag>
              <S.LegendButton
                type="text"
                size="small"
                icon={getIcon()}
                $color={color}
                $isVisible={isVisible}
                onClick={() => {
                  if (!isVisible) {
                    handleCategoryToggle(category);
                  } else {
                    handleCategoryIsolate(category);
                  }
                }}
              />
            </S.LegendItem>
          );
        })}
      </S.LegendContainer>
    );
  };

  const renderChartContent = () => {
    if (isLoading) {
      return <Spin style={styles.spinner} />;
    }

    if (!hasData) {
      return (
        <Empty
          description={t('monthly-billing:notFoundMessage')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    return (
      <>
        {renderCustomLegend()}
        <Column
          style={{
            height: 400,
          }}
          appendPadding={[32, 16, 0, 0]}
          data={filteredChartData}
          xField="date"
          yField="value"
          seriesField="category"
          isStack
          xAxis={{
            label: {
              autoHide: true,
              autoRotate: false,
              style: {
                fill: theme?.colors?.text || '#000',
              },
            },
          }}
          yAxis={{
            label: {
              formatter: (val: string) => formatAxisY(val),
              style: {
                fill: theme?.colors?.text || '#000',
              },
            },
          }}
          legend={false}
          tooltip={{
            formatter: datum => {
              return {
                name: datum.category,
                value: formatCurrency(datum.value as number),
              };
            },
          }}
          color={getChartColor}
          columnStyle={{
            radius: [4, 4, 0, 0],
          }}
          interactions={[
            {
              type: 'element-active',
            },
          ]}
          annotations={chartAnnotations}
        />
      </>
    );
  };

  return (
    <S.ContainerRow gutter={[16, 16]}>
      <Col span={24}>
        <Row gutter={[16, 16]} justify="end">
          <Col span={24}>
            <S.CardContainer
              title={
                <div className="card-header">
                  <div className="card-title-box">
                    <div className="card-title">{title}</div>
                    <Tooltip title={tooltipText}>
                      <Button type="link" icon={<QuestionCircleOutlined />} />
                    </Tooltip>
                    <Filters isOpen={showFilters} onOpen={onToggleFilters} />
                  </div>
                </div>
              }
            >
              <Row gutter={[24, 24]}>
                <Col xs={24}>
                  <S.FiltersContainer>
                    <FiltersCard
                      isOpen={showFilters}
                      tableFilters={filtersElement}
                    />
                  </S.FiltersContainer>
                </Col>
                <Col xs={24}>{renderChartContent()}</Col>
              </Row>
            </S.CardContainer>
          </Col>
        </Row>
      </Col>
    </S.ContainerRow>
  );
};
